import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { palette } from "../../styles/palette";
import { typography } from "../../styles/typography";
import dayjs from "dayjs";
import "dayjs/locale/ko"; // 한국어 로케일 불러오기
import { fetchUserInfo, logout, withdraw } from "@/api/mypageApi";
import type { UserInfo } from "@/api/mypageApi";
import ConfirmContext from "@/context/confirm/ConfirmContext";
import { clearTokens } from "@/utils/tokenUtils";
import { useNavigate } from "react-router-dom";
import logger from "@/utils/logger";
// Day.js 한국어 로케일 설정
dayjs.locale("ko");

// 약관 링크 상수 추가
const AGREEMENT_LINKS = {
    service: "https://maket-b.notion.site/2b34493cf6904b43857d79bb1a77fa53?pvs=73",
    privacy: "https://maket-b.notion.site/28c577b66235405ab4a5b7b4432bd7ea?pvs=74",
    marketing: "https://maket-b.notion.site/522ecc9c54e44603bea1c2cec0aa0a39?pvs=73"
};

const MyPage: React.FC = () => {
    // 사용자 정보 상태
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    // Context에서 showPopup 가져오기
    const { showPopup } = useContext(ConfirmContext);

    // 회원탈퇴 처리 함수
    const handleWithdraw = () => {
        showPopup("회원탈퇴를 진행하시겠습니까?", {
            type: "confirm",
            onConfirm: async () => {
                try {
                    // 회원탈퇴 API 호출
                    await withdraw();

                    // 로컬 스토리지 토큰 삭제
                    clearTokens();

                    // 세션 스토리지 데이터 삭제
                    sessionStorage.clear();

                    // 쿠키 삭제 (있을 경우)
                    document.cookie.split(";").forEach(function (c) {
                        document.cookie = c
                            .replace(/^ +/, "")
                            .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
                    });

                    // 성공 메시지 표시
                    await showPopup("회원탈퇴가 완료되었습니다.", {
                        type: "alert"
                    });

                    // 로그인 페이지로 리다이렉트
                    navigate("/");
                } catch (error) {
                    logger.error("회원탈퇴 처리 중 오류 발생:", error);

                    // 에러 메시지 표시
                    showPopup("회원탈퇴 처리 중 오류가 발생했습니다. 다시 시도해주세요.", {
                        type: "alert"
                    });
                }
            }
        });
    };

    // 로그아웃 처리 함수
    const handleLogout = () => {
        showPopup("로그아웃 하시겠습니까?", {
            type: "confirm",
            onConfirm: async () => {
                try {
                    // 로그아웃 API 호출
                    await logout();

                    // 로컬 스토리지 토큰 삭제
                    clearTokens();

                    // 세션 스토리지 데이터 삭제
                    sessionStorage.clear();

                    // 홈 페이지로 리다이렉트
                    navigate("/");
                } catch (error) {
                    logger.error("로그아웃 처리 중 오류 발생:", error);

                    // 에러가 발생해도 클라이언트에서는 로그아웃 처리
                    clearTokens();
                    sessionStorage.clear();
                    navigate("/");
                }
            }
        });
    };

    // 사용자 정보 가져오기
    useEffect(() => {
        const getUserInfo = async () => {
            try {
                setLoading(true);
                const data = await fetchUserInfo();
                setUserInfo(data);
                setError(null);
            } catch (err) {
                logger.error("사용자 정보를 가져오는 중 오류 발생:", err);
                setError("사용자 정보를 불러올 수 없습니다. 다시 시도해주세요.");
            } finally {
                setLoading(false);
            }
        };

        getUserInfo();
    }, []);

    return (
        <Container>
            <HeaderSection>
                <Title>마이페이지</Title>
                <SettingContainer>{/* 여기에 추가 필터나 설정이 들어갈 수 있음 */}</SettingContainer>
            </HeaderSection>

            <ContentSection>
                {loading ? (
                    <LoadingMessage>정보를 불러오는 중입니다...</LoadingMessage>
                ) : error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : (
                    <>
                        <UserInfoCard>
                            <UserName>{userInfo?.nickname || "사용자"}</UserName>
                            <UserEmail>{userInfo?.loginId || "이메일 정보 없음"}</UserEmail>
                        </UserInfoCard>

                        <SettingSection>
                            <SectionTitle>계정</SectionTitle>
                            <MenuItemWrapper>
                                <MenuItem onClick={handleLogout}>
                                    <MenuItemText>로그아웃</MenuItemText>
                                    <ArrowIcon />
                                </MenuItem>
                                <MenuItem onClick={handleWithdraw}>
                                    <MenuItemText>회원탈퇴</MenuItemText>
                                    <ArrowIcon />
                                </MenuItem>
                            </MenuItemWrapper>
                        </SettingSection>

                        <SettingSection>
                            <SectionTitle>약관 및 정책</SectionTitle>
                            <MenuItemWrapper>
                                <MenuItem onClick={() => window.open(AGREEMENT_LINKS.service, "_blank")}>
                                    <MenuItemText>서비스 이용약관</MenuItemText>
                                    <ArrowIcon />
                                </MenuItem>
                                <MenuItem onClick={() => window.open(AGREEMENT_LINKS.privacy, "_blank")}>
                                    <MenuItemText>개인정보 처리방침</MenuItemText>
                                    <ArrowIcon />
                                </MenuItem>
                                <MenuItem onClick={() => window.open(AGREEMENT_LINKS.marketing, "_blank")}>
                                    <MenuItemText>마케팅 목적 개인정보 수집/이용 약관</MenuItemText>
                                    <ArrowIcon />
                                </MenuItem>
                                <MenuItem>
                                    <MenuItemText>버전정보</MenuItemText>
                                    <ArrowIcon />
                                </MenuItem>
                            </MenuItemWrapper>
                        </SettingSection>
                    </>
                )}
            </ContentSection>
        </Container>
    );
};

const Container = styled.div`
    position: relative;
    width: 100%;
    max-width: 430px;
    min-width: 320px;
    height: 100vh;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    background: ${palette.white};
    overflow-y: auto;
`;

const HeaderSection = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid ${palette.gray[200]};
    background-color: ${palette.white};
    padding-top: 12px;
`;

const Title = styled.h1`
    ${typography.title2}
    color: ${palette.gray[900]};
    text-align: center;
    margin-bottom: 16px;
`;

const SettingContainer = styled.div`
    padding: 0 16px 12px;
`;

const ContentSection = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    flex: 1;
    padding: 20px 16px;
    padding-bottom: 80px; /* 바텀 네비게이션 높이보다 크게 패딩 추가 */
`;

const LoadingMessage = styled.div`
    ${typography.body1}
    color: ${palette.gray[700]};
    margin: 40px 0;
    text-align: center;
`;

const ErrorMessage = styled.div`
    ${typography.body1}
    color: ${palette.red};
    margin: 40px 0;
    text-align: center;
`;

const UserInfoCard = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px;
    background-color: ${palette.white};
    border-radius: 8px;
    margin-bottom: 24px;
`;

const UserName = styled.div`
    ${typography.title1}
    color: ${palette.gray[900]};
    margin-bottom: 4px;
`;

const UserEmail = styled.div`
    ${typography.body2}
    color: ${palette.gray[600]};
`;

const SettingSection = styled.div`
    margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
    ${typography.title3}
    color: ${palette.gray[800]};
    margin-bottom: 16px;
`;

const MenuItemWrapper = styled.div`
    border: 1px solid ${palette.gray[200]};
    border-radius: 8px;
    overflow: hidden;
`;

const MenuItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 1px solid ${palette.gray[200]};

    &:last-child {
        border-bottom: none;
    }
`;

const MenuItemText = styled.div`
    ${typography.body1}
    color: ${palette.gray[900]};
`;

const ArrowIcon = styled.div`
    width: 24px;
    height: 24px;
    background-image: url("/icon/chevron-right.png");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
`;

export default MyPage;
